<template>
  <div class="ccmis-brand" :class="brandClasses">
    <!-- Compact sidebar / card layout -->
    <div
      v-if="useCardLayout"
      class="ccmis-brand-card"
      :class="{ 'ccmis-brand-card--collapsed': collapsed, 'ccmis-brand-card--sidebar': variant === 'sidebar' }"
    >
      <div class="ccmis-brand-body">
        <div class="ccmis-brand-icon-wrap" aria-hidden="true">
          <img
            :src="logoSrc"
            :srcset="logoSrcset"
            sizes="42px"
            alt=""
            class="ccmis-brand-icon"
            width="42"
            height="42"
            decoding="async"
          />
        </div>
        <div v-show="!collapsed" class="ccmis-brand-copy">
          <p class="ccmis-brand-title">UGNAY</p>
          <p class="ccmis-brand-subtitle">
            {{ variant === 'hero' ? 'Unified Governance Network for Analytics & Yield' : 'CHEDRO Caraga Platform' }}
          </p>
          <span v-if="showVersion && variant === 'sidebar'" class="ccmis-brand-version ccmis-brand-version--inline">
            v{{ appVersion }}
          </span>
          <p v-if="variant === 'hero'" class="ccmis-brand-region">CHEDRO Caraga · Region XIII</p>
        </div>
      </div>
      <span v-if="showVersion && variant === 'hero'" class="ccmis-brand-version">v{{ appVersion }}</span>
    </div>

    <!-- Collapsed sidebar — icon only -->
    <div v-else-if="variant === 'mark'" class="ccmis-brand-icon-wrap ccmis-brand-icon-wrap--solo">
      <img
        :src="logoSrc"
        :srcset="logoSrcset"
        sizes="42px"
        alt="UGNAY"
        class="ccmis-brand-icon"
        width="42"
        height="42"
        decoding="async"
      />
    </div>

    <!-- Full horizontal logo (optional fallback) -->
    <div v-else class="ccmis-brand-frame">
      <img
        :src="logoSrc"
        alt="UGNAY — CHEDRO Caraga Unified Governance Network"
        class="ccmis-brand-image"
      />
      <span v-if="showVersion" class="ccmis-brand-version">v{{ appVersion }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: "CcmisLogo",
  props: {
    variant: {
      type: String,
      default: "sidebar",
      validator: (value) => ["mark", "sidebar", "hero"].includes(value),
    },
    theme: {
      type: String,
      default: "dark",
      validator: (value) => ["dark", "light"].includes(value),
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    logoSrc() {
      return `${process.env.BASE_URL}ugnay-logo-512.png`;
    },
    logoSrc2x() {
      return `${process.env.BASE_URL}ugnay-logo@2x.png`;
    },
    logoSrcset() {
      return `${this.logoSrc} 512w, ${this.logoSrc2x} 1024w`;
    },
    brandClasses() {
      return [`ccmis-brand--${this.variant}`, `ccmis-brand--${this.theme}`];
    },
    useCardLayout() {
      return this.variant === "sidebar" || this.variant === "hero";
    },
    showVersion() {
      return this.variant !== "mark";
    },
    appVersion() {
      return this.$store.state.appVersion || "2.0";
    },
  },
};
</script>

<style scoped>
.ccmis-brand {
  display: block;
  width: 100%;
  min-width: 0;
}

/* Card layout (sidebar + login) */
.ccmis-brand-card {
  position: relative;
  width: 100%;
  padding: 0.72rem 0.65rem 0.68rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.07);
  transition: background 0.2s ease, border-color 0.2s ease;
}

.ccmis-brand-card--sidebar {
  padding: 0.65rem 0.6rem;
}

.ccmis-brand-card--collapsed {
  padding: 0;
  background: transparent !important;
  border: none !important;
  border-radius: 0;
  box-shadow: none !important;
}

.ccmis-brand-card--collapsed .ccmis-brand-body {
  justify-content: center;
  align-items: center;
  padding-right: 0;
  gap: 0;
}

.ccmis-brand-card--collapsed .ccmis-brand-icon-wrap {
  width: 34px;
  height: 34px;
  margin: 0 auto;
}

.ccmis-brand-body {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
}

.ccmis-brand-card--sidebar .ccmis-brand-body {
  align-items: flex-start;
}

.ccmis-brand-icon-wrap {
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  overflow: visible;
  border-radius: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: box-shadow 0.2s ease;
}

.ccmis-brand-icon-wrap--solo {
  margin: 0 auto;
}

.ccmis-brand-icon {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  image-rendering: auto;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
}

.ccmis-brand-copy {
  min-width: 0;
  line-height: 1.15;
}

.ccmis-brand-title {
  margin: 0;
  font-size: 0.84rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #ffffff;
  line-height: 1.2;
}

.ccmis-brand-subtitle {
  margin: 0.22rem 0 0;
  font-size: 0.52rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1.35;
  text-transform: uppercase;
  color: rgba(126, 184, 232, 0.92);
  text-decoration: none;
}

.ccmis-brand-region {
  margin: 0.28rem 0 0;
  font-size: 0.48rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #e8a43a;
}

.ccmis-brand-version {
  position: absolute;
  top: 0.42rem;
  right: 0.42rem;
  padding: 0.18rem 0.48rem;
  border-radius: 999px;
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  line-height: 1.2;
  color: #1c1408;
  background: #e8a43a;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.ccmis-brand-version--inline {
  position: static;
  display: inline-block;
  margin-top: 0.38rem;
  padding: 0.18rem 0.48rem;
  font-size: 0.58rem;
  box-shadow: none;
}

/* Hero / login */
.ccmis-brand--hero .ccmis-brand-card {
  padding: 1rem 0.9rem;
  max-width: 360px;
  margin: 0 auto;
}

.ccmis-brand--hero .ccmis-brand-body {
  padding-right: 4rem;
  gap: 0.75rem;
}

.ccmis-brand--hero .ccmis-brand-icon-wrap {
  width: 56px;
  height: 56px;
}

.ccmis-brand--hero .ccmis-brand-icon {
  width: 100%;
  height: 100%;
}

.ccmis-brand--hero .ccmis-brand-title {
  font-size: 1.05rem;
}

.ccmis-brand--hero .ccmis-brand-subtitle {
  font-size: 0.58rem;
}

.ccmis-brand--hero .ccmis-brand-region {
  font-size: 0.54rem;
}

.ccmis-brand--hero .ccmis-brand-version {
  top: 0.55rem;
  right: 0.55rem;
  font-size: 0.62rem;
  padding: 0.2rem 0.52rem;
}

/* Light theme (login) */
.ccmis-brand--light .ccmis-brand-card {
  background: linear-gradient(180deg, #0f1d36 0%, #15263f 100%);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 10px 28px rgba(21, 38, 63, 0.18);
}

/* Mark-only */
.ccmis-brand--mark {
  display: flex;
  justify-content: center;
}

/* Full image fallback */
.ccmis-brand-frame {
  position: relative;
  display: inline-block;
  max-width: 100%;
  line-height: 0;
}

.ccmis-brand-image {
  display: block;
  width: 100%;
  height: auto;
  max-width: 228px;
}

.ccmis-brand-frame .ccmis-brand-version {
  background: rgba(0, 0, 0, 0.72);
  color: #e8a43a;
  border: 1px solid rgba(232, 164, 58, 0.45);
}
</style>
